import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandPlot.css';
import { FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaMap, FaLayerGroup, FaRuler } from 'react-icons/fa';

export default function LandPlot() {
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [cardWidth, setCardWidth] = useState(410);

  // Calculate card width on mount and resize
  useEffect(() => {
    const calculateCardWidth = () => {
      if (window.innerWidth <= 480) {
        setCardWidth(315);
      } else if (window.innerWidth <= 768) {
        setCardWidth(340);
      } else if (window.innerWidth <= 1024) {
        setCardWidth(370);
      } else {
        setCardWidth(410);
      }
    };

    calculateCardWidth();
    window.addEventListener('resize', calculateCardWidth);
    return () => window.removeEventListener('resize', calculateCardWidth);
  }, []);

  // Land & Plot property data
  const properties = [
    {
      id: 1,
      slug: 'chattarpur-residential-plot',
      title: 'Residential Plot',
      location: 'Chattarpur, New Delhi',
      type: 'Residential',
      builder: 'DLF Land',
      floors: 'Vacant Land',
      area: '500 sq.yd',
      category: 'Land & Plot',
      image: 'https://images.pexels.com/photos/259620/pexels-photo-259620.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 2,
      slug: 'gurgaon-commercial-plot',
      title: 'Commercial Plot',
      location: 'Gurgaon, Haryana',
      type: 'Commercial',
      builder: 'Prestige Land',
      floors: 'Freehold',
      area: '1000 sq.yd',
      category: 'Land & Plot',
      image: 'https://images.pexels.com/photos/164338/pexels-photo-164338.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 3,
      slug: 'greater-noida-industrial-land',
      title: 'Industrial Land',
      location: 'Greater Noida, UP',
      type: 'Industrial',
      builder: 'Oberoi Land',
      floors: 'Open Land',
      area: '5000 sq.yd',
      category: 'Land & Plot',
      image: 'https://images.pexels.com/photos/2475878/pexels-photo-2475878.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 4,
      slug: 'faridabad-residential-land',
      title: 'Residential Land',
      location: 'Faridabad, Haryana',
      type: 'Residential',
      builder: 'Tata Land',
      floors: 'Vacant Plot',
      area: '300 sq.yd',
      category: 'Land & Plot',
      image: 'https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 5,
      slug: 'sohna-road-farm-land',
      title: 'Farm Land',
      location: 'Sohna Road, Gurgaon',
      type: 'Agricultural',
      builder: 'Lodha Land',
      floors: 'Open Land',
      area: '2 Acre',
      category: 'Land & Plot',
      image: 'https://images.pexels.com/photos/974314/pexels-photo-974314.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 6,
      slug: 'manesar-institutional-plot',
      title: 'Institutional Plot',
      location: 'Manesar, Gurgaon',
      type: 'Institutional',
      builder: 'Godrej Land',
      floors: 'Freehold',
      area: '1500 sq.yd',
      category: 'Land & Plot',
      image: 'https://images.pexels.com/photos/221540/pexels-photo-221540.jpeg?auto=compress&cs=tinysrgb&w=800'
    }
  ];

  const scroll = (direction) => {
    const container = scrollRef.current;
    
    if (direction === 'left') {
      container.scrollBy({ left: -cardWidth, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <>
      <section className="landplot-section">
        <div className="landplot-container">
          
          {/* Header */}
          <div className="section-header">
            <h2>Latest Land & Plot Properties</h2>
            <p>Explore our premium land and plot options for your investment</p>
          </div>

          {/* Properties Slider */}
          <div className="slider-wrapper">
            <button 
              className="slider-btn slider-btn-left" 
              onClick={() => scroll('left')}
              aria-label="Scroll Left"
            >
              <FaChevronLeft />
            </button>

            <div 
              className="properties-slider" 
              ref={scrollRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {properties.map((property, index) => (
                <Link 
                  to={`/properties/land-plot/${property.slug}`}
                  key={property.id}
                  style={{ textDecoration: 'none' }}
                >
                  <div 
                    className="property-card"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="property-image">
                      <img src={property.image} alt={property.title} />
                      <div className="property-badge">{property.category}</div>
                    </div>

                    <div className="property-content">
                      <h3>{property.title}</h3>
                      
                      <div className="property-details">
                        <div className="detail-item">
                          <FaMapMarkerAlt className="detail-icon" />
                          <span>{property.location}</span>
                        </div>
                        
                        <div className="detail-item">
                          <FaMap className="detail-icon" />
                          <span>{property.type}</span>
                        </div>
                        
                        <div className="detail-item">
                          <FaRuler className="detail-icon" />
                          <span>{property.area}</span>
                        </div>
                        
                        <div className="detail-item">
                          <FaLayerGroup className="detail-icon" />
                          <span>{property.floors}</span>
                        </div>
                      </div>

                      <div className="property-footer">
                        <div className="price-tag">Price on Request</div>
                        <button className="contact-btn">View Details</button>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <button 
              className="slider-btn slider-btn-right" 
              onClick={() => scroll('right')}
              aria-label="Scroll Right"
            >
              <FaChevronRight />
            </button>
          </div>

          {/* View All Button */}
          <div className="view-all-wrapper">
            <Link to="/contact">
              <button className="view-all-btn">Contact Us</button>
            </Link>
          </div>

        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="contact-cta-section">
        <div className="cta-content">
          <h2>Looking for the Perfect Land or Plot?</h2>
          <p>Connect with our land investment experts today</p>
          <Link to="/contact" className="cta-link-btn">
            Get In Touch
          </Link>
        </div>
      </section>
    </>
  );
}
